import { AsyncSection } from "../components/AsyncSection";
import { BoardIllustration } from "../components/BoardIllustration";
import { Card } from "../components/Card";
import { ColdStartInvite } from "../components/ColdStartInvite";
import { Container } from "../components/Container";
import { DealEntry } from "../components/DealEntry";
import { DisclosureNote } from "../components/DisclosureNote";
import { SectionHeading } from "../components/SectionHeading";
import { Skeleton } from "../components/Skeleton";
import { HOME_SECTIONS } from "../lib/routes";
import { BoardDateline } from "./BoardDateline";
import styles from "./TodayBoard.module.css";
import type { BoardResponse } from "../api/types";
import type { Resource } from "../hooks/useApiResource";

/**
 * Today's Top 10 — the artifact the whole site is built around.
 *
 * Canon § 7 makes this a *finalized* board: the positions were decided by yesterday's
 * auction and are locked for the whole display day. Nothing here recomputes a ranking; the
 * order is the order core returns.
 *
 * The disclosure is the board's own top border — a brass band between the heading and rank
 * 1 — because canon § 12 requires the paid basis of the ranking to be visible where the
 * ranking is. `WEB-006` moved this board above the fold by reclaiming vertical space from
 * everything around it; the disclosure is the one thing that space could not come from, so
 * it grew rather than shrank.
 *
 * A short board is a designed state, not a failure. Canon § 6 keeps the first ten approved
 * deals exposed while the marketplace is small, so an under-filled board explains its own
 * capacity instead of padding itself out or apologising.
 *
 * An *empty* board has two readers and, since `WEB-016`, answers both. `WEB-023` answers
 * them in one card over a drawing of the ten positions: the explanation is still addressed to
 * the buyer and still comes first, and the founder — the only visitor who can change the
 * state — is asked in the same breath rather than below a rule. See
 * `src/components/ColdStartInvite.tsx` for what canon allows that card to offer, which is
 * less than a marketing instinct would reach for, and `src/components/BoardIllustration.tsx`
 * for why the rows behind it are line-work rather than anything obscured.
 *
 * ## The board between empty and full (`WEB-040`)
 *
 * `WEB-023` drew ten places on an empty board and one row on a board with one deal, so the
 * first approval replaced a picture of the marketplace with a list of length one and took the
 * explanation away with it. Canon § 6 makes that middle the *ordinary* state of a young
 * Dealbid — every approved deal is seated, free, in approval order — and `D-017` makes it
 * reachable within minutes of an approval rather than at the next boundary. It is now drawn
 * as what it is: the real deals, then the places still to be filled, then the card that says
 * why the board stops there.
 *
 * **Scoped to cold start, deliberately.** Outside it, a short board is a day the auction ran
 * and did not sell out (canon § 7, § 9), and drawing ten places there would advertise unsold
 * inventory to buyers who came for deals. The state is read from `disclosure.top10.code`,
 * which core already derives from the absence of any `auction` position — not re-derived
 * here, because two answers to "is this cold start" is how the disclosure and the layout come
 * to disagree.
 */
export function TodayBoard({ resource }: { resource: Resource<BoardResponse> }) {
  const board = resource.state.status === "ready" ? resource.state.data : null;
  /*
   * Canon § 8: the board is a dated artifact on a UTC day, so its dateline states the day
   * and the boundary as facts. Not a countdown — § 11 rules out manufactured urgency, and
   * this line sits directly above the deals.
   *
   * It is a component rather than a string because it also carries the stale-board guard
   * (`WEB-043`, canon `D-019`): if the boundary build did not run, the day is an earlier
   * one and the boundary has already passed, so the line says so instead of asserting both.
   * The clock subscription lives inside it, which keeps the per-second tick off this
   * section.
   */
  const dateline = <BoardDateline displayDate={board === null ? null : board.displayDate} />;

  return (
    <section className={styles.section} aria-labelledby="today">
      <Container>
        {/*
         * The page's h1 (WEB-006). The board is what dealbid.app is; the explanation of
         * what Dealbid is now sits below it and is reached from the aside link, so a
         * first-time visitor is one Tab stop from it without every visitor paying for it
         * in vertical space.
         *
         * The heading carries no description on purpose: the sentence that used to sit
         * here moved to "What is Dealbid?", because a paragraph between the title and
         * rank 1 is exactly the fold budget this task exists to reclaim. The disclosure
         * below is *not* that paragraph and does not move.
         */}
        <SectionHeading
          id="today"
          level={1}
          compact
          meta={dateline}
          title="Today’s Top 10"
          aside={
            /*
             * The canvas's downward arrow is right again: canon `D-007` put the explanation
             * back on this page, below the board, so the link really is a scroll. An arrow
             * that promises a scroll and delivers a navigation would be a small lie about
             * where a link goes, which is why `D-006` had turned it sideways.
             */
            <a className={styles.explainerLink} href={HOME_SECTIONS.whatIsDealbid}>
              New here? What is Dealbid ↓
            </a>
          }
        />

        <AsyncSection
          state={resource.state}
          reload={resource.reload}
          loadingLabel="Loading today’s board"
          errorTitle="Today’s board didn’t load"
          skeleton={<BoardSkeleton />}
        >
          {(data) => {
            const filled = data.top10.length;
            // Core's own answer, not a second one. See the module comment.
            const coldStart = data.disclosure.top10.code === "DAILY_TOP_10_COLD_START";
            /*
              The first rank still to be drawn, or `null` for a board that draws none.

              An empty board draws all ten whatever the disclosure says — that is `WEB-023`'s
              state and core cannot in practice send anything else for it, since a board with
              no `auction` position is cold start by definition. A partly filled board draws
              the remainder only during cold start: see the module comment for why a short
              sponsored board stays short.
            */
            const drawFrom =
              filled === 0
                ? 1
                : coldStart && filled < data.capacity.top10
                  ? filled + 1
                  : null;

            return (
              <>
                <DisclosureNote disclosure={data.disclosure.top10} prominent />

                {filled > 0 ? (
                  <ol className={styles.board} role="list">
                    {data.top10.map((entry) => (
                      <li key={entry.deal.id}>
                        {/*
                          Positions 1, 2 and 3 are stepped, the rest are compact — the design
                          canvas's tiering, restored by WEB-007 after the first build rendered
                          ten identical rows. The weight goes on the rank mark and the price,
                          never on a claim about the deal: canon § 12 forbids presenting a
                          high bid as an editorial judgement, and the brass band above says so
                          in words for every row underneath it.
                        */}
                        <DealEntry entry={entry} headingLevel={2} />
                      </li>
                    ))}
                  </ol>
                ) : null}

                {/*
                  The capacity sentence, worded for the board it is describing (`WEB-040`).

                  "Dealbid leaves the rest empty rather than filling them with deals that did
                  not qualify" is true of a day the auction ran and did not sell out. It is
                  false during cold start, where nothing was rejected and there are simply not
                  ten approved deals yet — and it is the sentence a founder reading a
                  three-deal board would most reasonably take as "Dealbid turned seven people
                  away". The count itself is unchanged and stays here rather than moving into
                  the invitation card: this is a description of the board, and the same figure
                  inside recruitment copy reads as scarcity.
                */}
                {filled > 0 && filled < data.capacity.top10 ? (
                  <p className={styles.capacity}>
                    <span className="figure">
                      {String(filled)} of {String(data.capacity.top10)}
                    </span>{" "}
                    positions are filled today.{" "}
                    {coldStart
                      ? "Dealbid is still approving its first deals, and lists every one of them free while the board has room."
                      : "Dealbid leaves the rest empty rather than filling them with deals that did not qualify."}
                  </p>
                ) : null}

                {/*
                  The board drawn rather than described (`WEB-023`, extended by `WEB-040`).
                  `WEB-016` answered the two readers of an empty board in two stacked blocks;
                  the cold-start canvas merges them into one card over an illustration of the
                  positions, so the founder who can fill the board sees what they would be
                  joining.

                  On an empty board the card sits over the drawing. With deals above it, it
                  sits in the flow beneath them — a buyer meets the deals first (canon § 13)
                  and the card becomes the boundary between the board and the places it has
                  not filled. Both parts disappear together when the board is full.

                  The illustration is line-work and says so in words — see
                  `src/components/BoardIllustration.tsx` for why an obscured row would be
                  `AGENTS.md` § 11's fake scarcity in visual form.
                */}
                {drawFrom !== null ? (
                  <div
                    className={`${styles.coldStart} ${filled > 0 ? styles.continued : ""}`.trim()}
                  >
                    <ColdStartInvite overlay={filled === 0} />
                    <BoardIllustration from={drawFrom} />
                  </div>
                ) : null}
              </>
            );
          }}
        </AsyncSection>
      </Container>
    </section>
  );
}

function BoardSkeleton() {
  return (
    <div className={styles.skeleton}>
      {[0, 1, 2].map((row) => (
        <Card key={row}>
          <div className={styles.skeletonRow}>
            <Skeleton width="30%" height="0.75rem" />
            <Skeleton height="1.5rem" />
            <Skeleton width="60%" />
          </div>
        </Card>
      ))}
    </div>
  );
}
